import React, { useMemo } from 'react';
import classNames from 'classnames';
import RadioGroupField from 'components/RadioGroupField';
import { useFormState } from 'react-final-form';
import {
  WorkflowAutomationValues,
  STEP_COMPLETION_ACTION,
  FILES_AVAILABILITY_MODE,
} from 'models/WorkflowConfiguration';
import { useTranslation } from 'react-i18next';
import { LEAD_STAGES, LEAD_STAGES_COPY, LeadStages } from 'models/Lead';
import { getIsCompletionAction } from '../WorkflowTemplate/utils';
import AutomationQuestion from '../AutomationQuestion';
import { CheckboxGroupPlaceholders } from '../WorkflowTemplate/checkboxplaceholders';
import useLeadStagesCopy from 'hooks/useLeadStagesCopy';
import useMatchMedia from 'hooks/useMatchMedia';
import { RULES_SELECTOR } from '../../data';
import WorkflowGuideAnchor from '../WorkflowGuideAnchor';

import './AutomationProgresser.styles.scss';

const NEXT_STATUS = {
  [LEAD_STAGES.ENQUIRE]: LEAD_STAGES_COPY.ACCEPTED,
  [LEAD_STAGES.ACCEPT]: LEAD_STAGES_COPY.PROSPECT,
  [LEAD_STAGES.CONFIRM]: LEAD_STAGES_COPY.BIDDER,
  [LEAD_STAGES.OFFER]: '',
} as const;

type AutomationProgresserProps = {
  className?: string;
  isLoading?: boolean;
  page: LeadStages;
};

const AutomationProgresser: React.FC<AutomationProgresserProps> = (props) => {
  const { className, page, isLoading } = props;

  const classes = classNames('automation-progresser', className);

  const { t } = useTranslation();
  const {
    values: { completionAction, filesAvailabilityMode },
  } = useFormState<WorkflowAutomationValues>();

  const copies = useLeadStagesCopy().getCopies();

  const isAbovePhablet = useMatchMedia('isAbovePhablet');

  const nextStatus = t(NEXT_STATUS[page]);

  const isLastStage = !nextStatus;

  const isOneAtATime =
    filesAvailabilityMode === FILES_AVAILABILITY_MODE.ONE_AT_A_TIME;

  const options = useMemo(
    () => [
      {
        label: t('keepInStatus', { status: t(copies[page]) }),
        value: STEP_COMPLETION_ACTION.KEEP,
      },
      {
        label: t('askProgressionQuestion'),
        value: STEP_COMPLETION_ACTION.ASK_PROGRESSION_QUESTION,
      },
      ...(isLastStage
        ? []
        : [
            {
              label: t('moveToStatusAutomatically', { status: nextStatus }),
              value: STEP_COMPLETION_ACTION.AUTO_ADVANCE,
            },
          ]),
    ],
    [isLastStage, nextStatus, page, copies, t],
  );

  // "Everything at once" keeps the last stage without a completion step.
  if (isLastStage && !isOneAtATime) return null;

  return (
    <div className={classes}>
      <div className="automation-progresser__content">
        <p className="wft-title">
          {t(
            isOneAtATime
              ? 'whenTheSequenceIsComplete'
              : 'whenAllStageFilesAreAccessed',
          )}
          <WorkflowGuideAnchor
            id={RULES_SELECTOR}
            title="decideWhatHappensNext"
            description={
              isLastStage
                ? 'decideWhatHappensNextLastStageDescription'
                : 'decideWhatHappensNextDescription'
            }
          />
        </p>

        {isLoading ? (
          <CheckboxGroupPlaceholders />
        ) : (
          <RadioGroupField
            name="completionAction"
            options={options}
            direction={isAbovePhablet ? 'row' : 'column'}
          />
        )}
      </div>

      {!isLoading && getIsCompletionAction(completionAction) && (
        <AutomationQuestion status={nextStatus} isLastStage={isLastStage} />
      )}
    </div>
  );
};

export default AutomationProgresser;
